import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { font } from '../../utils/fontHelper';
import { hapticLight } from '../../utils/nativeHaptics';

interface TabOption {
  label: string;
  value: number;
  icon?: React.ReactNode;
}

interface CustomTabsProps {
  value: number;
  onChange: (newValue: number) => void;
  onReselect?: (value: number) => void;
  tabs: TabOption[];
  size?: 'default' | 'large';
  /** Allow tabs to shrink and truncate labels with ellipsis when container is narrow */
  compressible?: boolean;
  sx?: any;
}

const CustomTabs: React.FC<CustomTabsProps> = ({ value, onChange, onReselect, tabs, size = 'default', compressible = false, sx = {} }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isLarge = size === 'large';

  return (
    <Box
      sx={{
        display: 'flex',
        gap: { xs: 1, sm: 1, md: 1.5 },
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollbarWidth: 'none', // Firefox
        '&::-webkit-scrollbar': {
          display: 'none', // Chrome, Safari, Edge
        },
        minWidth: 0,
        ...sx
      }}
    >
      {tabs.map((tab) => {
        const isSelected = value === tab.value;
        
        return (
          <Box
            key={tab.value}
            onClick={() => { hapticLight(); if (value === tab.value) { onReselect?.(tab.value); } else { onChange(tab.value); } }}
            sx={{
              px: 1.5, // 12px padding as per Figma
              py: 0,
              height: isLarge ? 44 : 40,
              borderRadius: 100, // Increased border radius to 16px
              border: isSelected 
                ? `1px solid ${isDark ? 'rgba(231, 225, 255, 1)' : 'rgba(60,60,67,0.18)'}` 
                : `0px solid ${isDark ? '#545456' : '#C6C6C8'}`,
              bgcolor: isSelected 
                ? (isDark ? 'rgba(136, 109, 243, 0)' : '#000000') 
                : (isDark ? 'rgba(255, 255, 255, 0.1)' : '#000000') ,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              flex: compressible ? '0 1 auto' : '0 0 auto',
              minWidth: compressible ? (tab.icon ? 52 : 40) : 'auto',
              '&:hover': {
                bgcolor: isSelected 
                  ? (isDark ? '#ffffff17' : '#000000') 
                  : (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'),
              }
            }}
          >
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: tab.icon ? isLarge ? 0.75 : 0.5 : 0,
              minWidth: 0,
              overflow: 'hidden',
            }}>
              {tab.icon && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexShrink: 0,
                  color: isSelected
                    ? '#FFFFFF'
                    : theme.palette.text.primary,
                  '& svg': {
                    fontSize: isLarge ? 18 : 16,
                  }
                }}>
                  {tab.icon}
                </Box>
              )}
              <Typography sx={{
                fontFamily: font(),
                fontWeight: isSelected
                  ? (isDark ? 500 : 600)
                  : (isDark ? 400 : 500),
                fontSize: isLarge ? 15 : 14,
                lineHeight: 1.5,
                color: isSelected
                  ? '#FFFFFF'
                  : theme.palette.text.primary,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                textAlign: 'center',
                minWidth: 0,
              }}>
                {tab.label}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default CustomTabs; 